import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import UserAvatar from '../components/UserAvatar';
import CpaContactCard from '../components/CpaContactCard';
import { Separator } from '../components/ui/separator';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import {
  User, Building, Mail, LogOut, Heart, Upload, FileText,
  Trash2, DollarSign, PiggyBank, Loader2, CheckCircle, Eye, MapPin, Briefcase, Globe,
  Receipt, TrendingUp, Plus, Shield} from 'lucide-react';
import { logError } from '../utils/logger';
import axios from 'axios';
import LanguageSwitcher from '../components/LanguageSwitcher';
import GDPRPrivacySettings from '../components/GDPRPrivacySettings';
import TrustedDevicesCard from '../components/TrustedDevicesCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Settings = () => {
  const { t } = useTranslation();
  const { user, logout, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const tab = params.get('tab');
    if (tab === 'privacy')     return 'privacy';
    if (tab === 'tax-profile') return 'tax-profile';
    return 'settings';
  });

  // ── Tax Profile state (profession + prior year tax) ───────────────────────
  const [profession, setProfession]             = useState(user?.profession || '');
  const [priorYearTax, setPriorYearTax]         = useState(
    user?.prior_year_tax_paid ? String(user.prior_year_tax_paid) : ''
  );
  const [professions, setProfessions]           = useState([]);
  const [savingProfession, setSavingProfession] = useState(false);
  const [businessType, setBusinessType] = useState(user?.business_type || 'sole_proprietor');
  
  // Tax Profile State
  const [filingStatus, setFilingStatus] = useState(user?.filing_status || 'single');
  const [filingStatusOptions, setFilingStatusOptions] = useState([]);
  const [selectedState, setSelectedState] = useState(user?.state || '');
  const [stateInfo, setStateInfo] = useState(null);
  const [states, setStates] = useState([]);
  const [spouseInfo, setSpouseInfo] = useState(null);
  const [spouseDocuments, setSpouseDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [extracting, setExtracting] = useState(null);
  
  // W-2 Worker State
  const [hasW2Income, setHasW2Income] = useState(false);
  const [w2Entries, setW2Entries] = useState([]);        // multiple W-2s per year
  const [w2Documents, setW2Documents] = useState([]);
  const [showW2Form, setShowW2Form] = useState(false);
  const [editingW2Id, setEditingW2Id] = useState(null);  // null = add mode, string = edit mode
  const [w2TaxYear, setW2TaxYear] = useState(new Date().getFullYear() - 1);
  const [w2Form, setW2Form] = useState({
    taxpayer: 'filer',
    employer_name: '', employer_ein: '', employer_address: '', employee_ssn_last4: '',
    w2_wages: 0, federal_withholding: 0,
    ss_wages: 0, ss_tax_withheld: 0, medicare_wages: 0, medicare_tax_withheld: 0,
    ss_tips: 0, allocated_tips: 0,
    dependent_care_benefits: 0, nonqualified_plans: 0,
    retirement_401k: 0, roth_401k: 0, roth_403b: 0,
    hsa_contributions: 0, employer_health_coverage: 0,
    retirement_ira: 0, fsa_contributions: 0,
    statutory_employee: false, retirement_plan: false, third_party_sick_pay: false,
    box14_other: '',
    employer_state: '', employer_state_id: '',
    state_wages: 0, state_withholding: 0,
    local_wages: 0, local_tax_withheld: 0, locality_name: '',
    notes: ''
  });

  // 1099 Income State
  const [form1099Documents, setForm1099Documents] = useState([]);
  const [form1099Summary, setForm1099Summary] = useState({ total: 0, count: 0 });
  const [uploading1099, setUploading1099] = useState(false);
  const [extracting1099, setExtracting1099] = useState(null);
  const [showManual1099Form, setShowManual1099Form] = useState(false);
  const [manual1099Form, setManual1099Form] = useState({
    payer_name: '', amount: 0, box_type: 'NEC', tax_year: new Date().getFullYear() - 1,
    federal_withholding: 0, state_withholding: 0, notes: ''
  });

  // Spouse Form State
  const [spouseTaxYear, setSpouseTaxYear] = useState(new Date().getFullYear() - 1);
  const [spouseDocYear, setSpouseDocYear] = useState(new Date().getFullYear() - 1);
  const [spouse1099s, setSpouse1099s] = useState([]);
  const [spouse1099TotalIncome, setSpouse1099TotalIncome] = useState(0);
  const [showSpouseManual1099Form, setShowSpouseManual1099Form] = useState(false);
  const [spouseManual1099Form, setSpouseManual1099Form] = useState({
    payer_name: '', amount: 0, box_type: 'NEC', tax_year: new Date().getFullYear() - 1,
    federal_withholding: 0, state_withholding: 0, notes: ''
  });
  const [spouseForm, setSpouseForm] = useState({
    name: '',
    ssn_last4: '',
    employer_name: '',
    w2_wages: 0,
    federal_withholding: 0,
    state_withholding: 0,
    self_employment_income: 0,
    retirement_401k: 0,
    retirement_ira: 0,
    hsa_contributions: 0,
    fsa_contributions: 0,
    other_income: 0,
    notes: ''
  });
  
  const [showSpouseForm, setShowSpouseForm] = useState(false);
  const [confirmDeleteAction, setConfirmDeleteAction] = useState(null);
  // null | "w2entry-{id}" | "w2doc-{id}" | "1099doc-{id}" | "spousedoc-{id}" | "spouse1099-{id}" | "spouseinfo"

  const fetchTaxProfile = useCallback(async () => {
    setLoading(true);
    try {
      const [profileRes, statesRes] = await Promise.all([
        axios.get(`${API}/user/tax-profile`, { headers: getAuthHeaders() }),
        axios.get(`${API}/states`)
      ]);
      
      setFilingStatus(profileRes.data.filing_status);
      setFilingStatusOptions(profileRes.data.filing_status_options);
      setSelectedState(profileRes.data.state || '');
      setStateInfo(profileRes.data.state_info);
      setSpouseInfo(profileRes.data.spouse_info);
      setSpouseDocuments(profileRes.data.spouse_documents || []);
      // Fetch spouse 1099s separately
      try {
        const sp1099Res = await axios.get(`${API}/user/spouse-1099`, { headers: getAuthHeaders() });
        setSpouse1099s(sp1099Res.data.entries || []);
        // ── Commandment II: total_income from backend DTO ──
        setSpouse1099TotalIncome(sp1099Res.data.total_income ?? 0);
      } catch { setSpouse1099s([]); }
      setStates(statesRes.data.states);
      
      // Load W-2 entries (new multi-entry endpoint)
      setHasW2Income(profileRes.data.has_w2_income || false);
      setW2Documents(profileRes.data.w2_documents || []);
      // Fetch all W-2 entries separately
      try {
        const w2Res = await axios.get(`${API}/user/w2-entries`, { headers: getAuthHeaders() });
        setW2Entries(w2Res.data.entries || []);
      } catch { setW2Entries([]); }
      
      // Load 1099 documents
      setForm1099Documents(profileRes.data.form_1099_documents || []);
      setForm1099Summary(profileRes.data.form_1099_summary || { total: 0, count: 0 });

      if (profileRes.data.spouse_info) {
        setSpouseForm({
          name: profileRes.data.spouse_info.name || '',
          ssn_last4: profileRes.data.spouse_info.ssn_last4 || '',
          employer_name: profileRes.data.spouse_info.employer_name || '',
          w2_wages: profileRes.data.spouse_info.w2_wages || 0,
          federal_withholding: profileRes.data.spouse_info.federal_withholding || 0,
          state_withholding: profileRes.data.spouse_info.state_withholding || 0,
          self_employment_income: profileRes.data.spouse_info.self_employment_income || 0,
          retirement_401k: profileRes.data.spouse_info.retirement_401k || 0,
          retirement_ira: profileRes.data.spouse_info.retirement_ira || 0,
          hsa_contributions: profileRes.data.spouse_info.hsa_contributions || 0,
          fsa_contributions: profileRes.data.spouse_info.fsa_contributions || 0,
          other_income: profileRes.data.spouse_info.other_income || 0,
          notes: profileRes.data.spouse_info.notes || ''
        });
      }
    } catch (error) {
      logError(error, { context: 'Settings.fetchTaxProfile' });
      // Still try to load states even if profile fails
      try {
        const statesRes = await axios.get(`${API}/states`);
        setStates(statesRes.data.states || []);
        // Set default filing status options
        setFilingStatusOptions([
          { value: 'single', label: 'Single' },
          { value: 'married_filing_jointly', label: 'Married Filing Jointly' },
          { value: 'married_filing_separately', label: 'Married Filing Separately' },
          { value: 'head_of_household', label: 'Head of Household' }
        ]);
      } catch (statesError) {
        logError(statesError, { context: 'Settings.fetchStates' });
      }
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchTaxProfile();
    // Fetch profession list from onboarding API
    axios.get(`${API}/user/onboarding/professions`, { withCredentials: true })
      .then(r => setProfessions(r.data?.professions || []))
      .catch(() => {});
  }, [fetchTaxProfile]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Tax Profile save ─────────────────────────────────────────────────────
  const handleSaveProfession = async () => {
    if (!profession) { toast.error(t('settings.select_profession', 'Please select a profession')); return; }
    setSavingProfession(true);
    try {
      await axios.put(`${API}/user/profession`, {
        profession,
        prior_year_tax_paid: priorYearTax ? parseFloat(priorYearTax) : undefined,
      }, { withCredentials: true });
      toast.success(t('settings.profession_saved', 'Tax profile saved'));
    } catch {
      toast.error(t('common.error', 'Could not save. Please try again.'));
    } finally {
      setSavingProfession(false);
    }
  };

  const handleLogout = async () => {    await logout();
    navigate('/');
    toast.success(t('toast.logged_out_successfully'));
  };

  const handleFilingStatusChange = async (newStatus) => {
    try {
      await axios.put(`${API}/user/filing-status`, 
        { filing_status: newStatus },
        { headers: getAuthHeaders() }
      );
      setFilingStatus(newStatus);
      toast.success(t('toast.filing_status_updated'));
      
      // Show spouse form if married filing jointly
      if (newStatus === 'married_filing_jointly' && !spouseInfo) {
        setShowSpouseForm(true);
      }
    } catch (error) {
      toast.error(t('toast.failed_to_update_filing_status'));
    }
  };

  const handleStateChange = async (newState) => {
    try {
      const response = await axios.put(`${API}/user/state`, 
        { state: newState },
        { headers: getAuthHeaders() }
      );
      setSelectedState(newState);
      setStateInfo(response.data.state_info);
      toast.success(t('toast.state_updated_to', { name: response.data.state_info.name }));
    } catch (error) {
      toast.error(t('toast.failed_to_update_state'));
    }
  };

  const handleSaveSpouseInfo = async () => {
    // ── Commandment VII: presence validation is the backend's responsibility ──
    setSaving(true);
    try {
      const response = await axios.put(`${API}/user/spouse`, { ...spouseForm, tax_year: spouseTaxYear }, {
        headers: getAuthHeaders()
      });
      setSpouseInfo(response.data.spouse);
      setShowSpouseForm(false);
      toast.success(t('toast.spouse_information_saved'));
      fetchTaxProfile();
    } catch (error) {
      toast.error(t('toast.failed_to_save_spouse_information'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSpouseInfo = async () => {
    const key = "spouseinfo";
    if (confirmDeleteAction !== key) {
      setConfirmDeleteAction(key);
      setTimeout(() => setConfirmDeleteAction(prev => prev === key ? null : prev), 3000);
      return;
    }
    setConfirmDeleteAction(null);
    try {
      await axios.delete(`${API}/user/spouse?year=${spouseTaxYear}`, { headers: getAuthHeaders() });
      setSpouseInfo(null); setFilingStatus('single');
      setSpouseForm({ name: '', ssn_last4: '', employer_name: '', w2_wages: 0, federal_withholding: 0, state_withholding: 0, self_employment_income: 0, retirement_401k: 0, retirement_ira: 0, hsa_contributions: 0, fsa_contributions: 0, other_income: 0, notes: '' });
      toast.success(t('toast.spouse_information_removed'));
    } catch { toast.error(t('toast.failed_to_remove_spouse_information')); }
  };

  const handleSpouseDocumentUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file type - only PDF allowed for reliable data extraction
    if (!file.type.includes('pdf') && !file.name.toLowerCase().endsWith('.pdf')) {
      toast.error(t('settings.w2_pdf_only', 'Please upload a PDF file. For best results, download your W-2 as a PDF from your employer portal.'));
      e.target.value = ''; // Reset file input
      return;
    }
    
    const formData = new FormData();
    formData.append('file', file);
    formData.append('document_type', 'w2');
    formData.append('tax_year', spouseDocYear);
    
    setUploading(true);
    try {
      await axios.post(`${API}/spouse/documents`, formData, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success(t('settings.document_upload_success', 'Document uploaded successfully'));
      fetchTaxProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('settings.document_upload_failed', 'Failed to upload document'));
    } finally {
      setUploading(false);
    }
  };

  const handleExtractData = async (documentId) => {
    setExtracting(documentId);
    try {
      const response = await axios.post(`${API}/spouse/documents/${documentId}/ocr`, {}, {
        headers: getAuthHeaders()
      });
      toast.success(t('toast.data_extracted_successfully'));
      
      // Update spouse form with extracted data if available
      const extracted = response.data.extracted_data;
      if (extracted) {
        setSpouseForm(prev => ({
          ...prev,
          employer_name: extracted.employer_name || prev.employer_name,
          w2_wages: extracted.wages || prev.w2_wages,
          federal_withholding: extracted.federal_tax_withheld || prev.federal_withholding,
          state_withholding: extracted.state_tax_withheld || prev.state_withholding
        }));
      }
      
      fetchTaxProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('toast.failed_to_extract_data'));
    } finally {
      setExtracting(null);
    }
  };

  const handleDeleteDocument = async (documentId) => {
    const key = `spousedoc-${documentId}`;
    if (confirmDeleteAction !== key) {
      setConfirmDeleteAction(key);
      setTimeout(() => setConfirmDeleteAction(prev => prev === key ? null : prev), 3000);
      return;
    }
    setConfirmDeleteAction(null);
    try {
      await axios.delete(`${API}/spouse/documents/${documentId}`, { headers: getAuthHeaders() });
      toast.success(t('toast.document_deleted'));
      fetchTaxProfile();
    } catch { toast.error(t('toast.failed_to_delete_document')); }
  };

  const handleW2IncomeToggle = async (checked) => {
    try {
      await axios.put(`${API}/user/w2-income`, { has_w2_income: checked }, { headers: getAuthHeaders() });
      setHasW2Income(checked);
      if (checked) setShowW2Form(true);
      toast.success(checked ? t('toast.w2_income_enabled') : t('toast.w2_income_disabled'));
    } catch { toast.error(t('toast.failed_to_update_w_2_status')); }
  };

  const handleEditW2Entry = (entry) => {
    setW2Form({
      taxpayer:               entry.taxpayer || 'filer',
      employer_name:          entry.employer_name || '',
      employer_ein:           entry.employer_ein || '',
      employer_address:       entry.employer_address || '',
      employee_ssn_last4:     entry.employee_ssn_last4 || '',
      w2_wages:               entry.w2_wages || 0,
      federal_withholding:    entry.federal_withholding || 0,
      ss_wages:               entry.ss_wages || 0,
      ss_tax_withheld:        entry.ss_tax_withheld || 0,
      medicare_wages:         entry.medicare_wages || 0,
      medicare_tax_withheld:  entry.medicare_tax_withheld || 0,
      ss_tips:                entry.ss_tips || 0,
      allocated_tips:         entry.allocated_tips || 0,
      dependent_care_benefits:entry.dependent_care_benefits || 0,
      nonqualified_plans:     entry.nonqualified_plans || 0,
      retirement_401k:        entry.retirement_401k || 0,
      roth_401k:              entry.roth_401k || 0,
      roth_403b:              entry.roth_403b || 0,
      hsa_contributions:      entry.hsa_contributions || 0,
      employer_health_coverage:entry.employer_health_coverage || 0,
      retirement_ira:         entry.retirement_ira || 0,
      fsa_contributions:      entry.fsa_contributions || 0,
      statutory_employee:     entry.statutory_employee || false,
      retirement_plan:        entry.retirement_plan || false,
      third_party_sick_pay:   entry.third_party_sick_pay || false,
      box14_other:            entry.box14_other || '',
      employer_state:         entry.employer_state || '',
      employer_state_id:      entry.employer_state_id || '',
      state_wages:            entry.state_wages || 0,
      state_withholding:      entry.state_withholding || 0,
      local_wages:            entry.local_wages || 0,
      local_tax_withheld:     entry.local_tax_withheld || 0,
      locality_name:          entry.locality_name || '',
      notes:                  entry.notes || '',
    });
    setW2TaxYear(entry.tax_year || new Date().getFullYear() - 1);
    setEditingW2Id(entry.entry_id);
    setShowW2Form(true);
  };

  const handleSaveW2Info = async () => {
    setSaving(true);
    try {
      if (editingW2Id) {
        // Edit mode — PUT
        await axios.put(`${API}/user/w2-entries/${editingW2Id}`, { ...w2Form, tax_year: w2TaxYear }, { headers: getAuthHeaders() });
        toast.success('W-2 entry updated successfully.');
      } else {
        // Add mode — POST
        await axios.post(`${API}/user/w2-entries`, { ...w2Form, tax_year: w2TaxYear }, { headers: getAuthHeaders() });
        toast.success(t('toast.w_2_information_saved'));
      }
      setShowW2Form(false);
      setEditingW2Id(null);
      setW2Form({ taxpayer: 'filer', employer_name: '', employer_ein: '', employer_address: '', employee_ssn_last4: '',
        w2_wages: 0, federal_withholding: 0, ss_wages: 0, ss_tax_withheld: 0,
        medicare_wages: 0, medicare_tax_withheld: 0, ss_tips: 0, allocated_tips: 0,
        dependent_care_benefits: 0, nonqualified_plans: 0,
        retirement_401k: 0, roth_401k: 0, roth_403b: 0,
        hsa_contributions: 0, employer_health_coverage: 0, retirement_ira: 0, fsa_contributions: 0,
        statutory_employee: false, retirement_plan: false, third_party_sick_pay: false,
        box14_other: '', employer_state: '', employer_state_id: '',
        state_wages: 0, state_withholding: 0, local_wages: 0, local_tax_withheld: 0,
        locality_name: '', notes: '' });
      const res = await axios.get(`${API}/user/w2-entries`, { headers: getAuthHeaders() });
      setW2Entries(res.data.entries || []);
    } catch { toast.error(t('toast.failed_to_save_w_2_information')); }
    finally { setSaving(false); }
  };

  const handleDeleteW2Entry = async (entryId) => {
    const key = `w2entry-${entryId}`;
    if (confirmDeleteAction !== key) {
      setConfirmDeleteAction(key);
      setTimeout(() => setConfirmDeleteAction(prev => prev === key ? null : prev), 3000);
      return;
    }
    setConfirmDeleteAction(null);
    try {
      await axios.delete(`${API}/user/w2-entries/${entryId}`, { headers: getAuthHeaders() });
      setW2Entries(prev => prev.filter(e => e.entry_id !== entryId));
      toast.success(t('toast.w_2_information_removed'));
    } catch { toast.error(t('toast.failed_to_remove_w_2_information')); }
  };

  const handleW2DocumentUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file type - only PDF allowed for reliable data extraction
    if (!file.type.includes('pdf') && !file.name.toLowerCase().endsWith('.pdf')) {
      toast.error(t('settings.w2_pdf_only', 'Please upload a PDF file. For best results, download your W-2 as a PDF from your employer portal.'));
      e.target.value = ''; // Reset file input
      return;
    }
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(`${API}/user/w2-documents`, formData, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' }
      });
      toast.success(t('settings.w2_upload_success', 'W-2 document uploaded successfully'));
      fetchTaxProfile();
    } catch (error) {
      toast.error(t('settings.w2_upload_failed', 'Failed to upload W-2 document'));
    } finally {
      setUploading(false);
    }
  };

  const handleExtractW2Data = async (documentId) => {
    setExtracting(documentId);
    try {
      const response = await axios.post(`${API}/user/w2-documents/${documentId}/extract`, {}, {
        headers: getAuthHeaders()
      });
      
      if (response.data.extracted_data) {
        setW2Form(prev => ({
          ...prev,
          ...response.data.extracted_data
        }));
        setShowW2Form(true);
        toast.success(t('toast.w_2_data_extracted_review_and_save_the_information'));
      }
    } catch (error) {
      toast.error(t('toast.failed_to_extract_w_2_data'));
    } finally {
      setExtracting(null);
    }
  };

  const handleDeleteW2Document = async (documentId) => {
    const key = `w2doc-${documentId}`;
    if (confirmDeleteAction !== key) {
      setConfirmDeleteAction(key);
      setTimeout(() => setConfirmDeleteAction(prev => prev === key ? null : prev), 3000);
      return;
    }
    setConfirmDeleteAction(null);
    try {
      await axios.delete(`${API}/user/w2-documents/${documentId}`, { headers: getAuthHeaders() });
      toast.success(t('toast.w_2_document_deleted'));
      fetchTaxProfile();
    } catch { toast.error(t('toast.failed_to_delete_w_2_document')); }
  };

  // 1099 Document Handlers
  const handle1099Upload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file type - only PDF allowed
    if (!file.type.includes('pdf') && !file.name.toLowerCase().endsWith('.pdf')) {
      toast.error(t('toast.please_upload_a_pdf_file_for_1099_documents'));
      e.target.value = '';
      return;
    }
    
    setUploading1099(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      await axios.post(`${API}/user/1099-documents`, formData, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' }
      });
      toast.success(t('toast.1099_document_uploaded_successfully'));
      fetchTaxProfile();
    } catch (error) {
      toast.error(t('toast.failed_to_upload_1099_document'));
    } finally {
      setUploading1099(false);
      e.target.value = '';
    }
  };

  const handleExtract1099 = async (documentId) => {
    setExtracting1099(documentId);
    try {
      const response = await axios.post(`${API}/user/1099-documents/${documentId}/extract`, {}, {
        headers: getAuthHeaders()
      });
      
      if (response.data.extracted_data) {
        toast.success(t('toast.extracted_data', { payer: response.data.extracted_data.payer_name || 'Unknown', amount: formatCurrency(response.data.extracted_data.amount) }));
        fetchTaxProfile();
      } else {
        toast.info(t('toast.could_not_extract_data_please_enter_manually'));
      }
    } catch (error) {
      toast.error(t('toast.failed_to_extract_1099_data'));
    } finally {
      setExtracting1099(null);
    }
  };

  const handleDelete1099Document = async (documentId) => {
    const key = `1099doc-${documentId}`;
    if (confirmDeleteAction !== key) {
      setConfirmDeleteAction(key);
      setTimeout(() => setConfirmDeleteAction(prev => prev === key ? null : prev), 3000);
      return;
    }
    setConfirmDeleteAction(null);
    try {
      await axios.delete(`${API}/user/1099-documents/${documentId}`, { headers: getAuthHeaders() });
      toast.success(t('toast.1099_document_deleted'));
      fetchTaxProfile();
    } catch { toast.error(t('toast.failed_to_delete_1099_document')); }
  };

  const handleAddManual1099 = async () => {
    // ── Commandment VII: required-field validation belongs to the backend Pydantic model ──
    try {
      await axios.post(`${API}/user/1099-manual`, manual1099Form, { headers: getAuthHeaders() });
      toast.success('1099 entry added.');
      setShowManual1099Form(false);
      setManual1099Form({ payer_name: '', amount: 0, box_type: 'NEC', tax_year: new Date().getFullYear() - 1, federal_withholding: 0, state_withholding: 0, notes: '' });
      fetchTaxProfile();
    } catch { toast.error('Failed to add 1099 entry.'); }
  };

  const handleAddSpouseManual1099 = async () => {
    // ── Commandment VII: required-field validation belongs to the backend Pydantic model ──
    try {
      const res = await axios.post(`${API}/user/spouse-1099-manual`, spouseManual1099Form, { headers: getAuthHeaders() });
      setSpouse1099s(prev => [res.data.entry, ...prev]);
      toast.success('Spouse 1099 entry added.');
      setShowSpouseManual1099Form(false);
      setSpouseManual1099Form({ payer_name: '', amount: 0, box_type: 'NEC', tax_year: new Date().getFullYear() - 1, federal_withholding: 0, state_withholding: 0, notes: '' });
    } catch { toast.error('Failed to add spouse 1099 entry.'); }
  };

  const handleDeleteSpouse1099 = async (entryId) => {
    const key = `spouse1099-${entryId}`;
    if (confirmDeleteAction !== key) {
      setConfirmDeleteAction(key);
      setTimeout(() => setConfirmDeleteAction(prev => prev === key ? null : prev), 3000);
      return;
    }
    setConfirmDeleteAction(null);
    try {
      await axios.delete(`${API}/user/spouse-1099/${entryId}`, { headers: getAuthHeaders() });
      setSpouse1099s(prev => prev.filter(e => e.document_id !== entryId));
      toast.success('Spouse 1099 entry deleted.');
    } catch { toast.error('Failed to delete spouse 1099 entry.'); }
  };

  const businessTypes = [
    { value: 'sole_proprietor', label: 'Sole Proprietor' },
    { value: 'llc', label: 'LLC' },
    { value: 's_corp', label: 'S-Corporation' },
    { value: 'c_corp', label: 'C-Corporation' },
    { value: 'partnership', label: 'Partnership' },
    { value: 'startup', label: 'Startup' }
  ];

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(value || 0);
  };

  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto space-y-6" data-testid="settings-page">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold mb-1">Settings</h1>
          <p className="text-muted-foreground">Manage your account, tax profile, and preferences</p>
        </div>

        {/* Tab navigation */}
        <div className="flex gap-1 border-b border-border" data-testid="settings-tabs">
          {[
            { id: 'settings',    label: 'Account & Tax',   icon: <User className="w-4 h-4" />        },
            { id: 'tax-profile', label: 'Tax Profile',     icon: <FileText className="w-4 h-4" />    },
            { id: 'privacy',     label: 'Privacy & Data',  icon: <Shield className="w-4 h-4" />      },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              data-testid={`settings-tab-${tab.id}`}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${
                activeTab === tab.id
                  ? 'border-gray-900 text-gray-900'
                  : 'border-transparent text-muted-foreground hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>

        {/* ── Privacy & Data tab ─────────────────────────────────────────────── */}
        {activeTab === 'privacy' && user?.is_admin && <TrustedDevicesCard />}
        {activeTab === 'privacy' && (
          <Card className="shadow-soft border-border/40" data-testid="privacy-tab-card">
            <CardContent className="pt-6">
              <GDPRPrivacySettings />
            </CardContent>
          </Card>
        )}

        {/* ── Tax Profile tab ────────────────────────────────────────────────── */}
        {activeTab === 'tax-profile' && (
          <Card className="shadow-soft border-border/40" data-testid="tax-profile-tab-card">
            <CardContent className="pt-6 space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {t('settings.tax_profile', 'Tax Profile')}
                </h3>
                <p className="text-sm text-muted-foreground mt-1">
                  {t('settings.tax_profile_desc',
                    'Your profession shapes deduction suggestions, peer benchmarks, and quarterly estimates.')}
                </p>
              </div>

              {/* Profession selector */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  {t('settings.profession', 'Profession')}
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="profession-grid">
                  {professions.map(({ value, label }) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setProfession(value)}
                      data-testid={`profession-${value}`}
                      className={`py-2.5 px-3 rounded-xl text-sm font-semibold border-2 transition-all text-left ${
                        profession === value
                          ? 'bg-gray-900 text-white border-gray-900'
                          : 'bg-white text-gray-700 border-gray-200 hover:border-gray-400'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                {profession && (
                  <p className="text-xs text-muted-foreground mt-2">
                    {t('settings.profession_selected', 'Selected')}: <strong>{
                      professions.find(p => p.value === profession)?.label || profession
                    }</strong>
                  </p>
                )}
              </div>

              {/* Prior year tax paid — used for quarterly safe-harbor */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  {t('settings.prior_year_tax', 'Prior Year Federal Tax Paid')}
                </label>
                <p className="text-xs text-muted-foreground mb-2">
                  {t('settings.prior_year_tax_desc',
                    'Total federal tax from your prior year return (Form 1040 Line 24). Used to calculate safe-harbor quarterly payments.')}
                </p>
                <input
                  type="number"
                  min="0"
                  value={priorYearTax}
                  onChange={e => setPriorYearTax(e.target.value)}
                  placeholder="e.g. 28000"
                  data-testid="prior-year-tax-input"
                  className="w-full max-w-xs border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
                />
              </div>

              {/* Save */}
              <div className="flex items-center gap-3 pt-2 border-t border-gray-100">
                <Button
                  onClick={handleSaveProfession}
                  disabled={savingProfession || !profession}
                  data-testid="save-profession-btn"
                  className="min-w-[120px]"
                >
                  {savingProfession
                    ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t('common.saving', 'Saving…')}</>
                    : t('common.save_changes', 'Save Changes')
                  }
                </Button>
                {profession && (
                  <span className="text-xs text-muted-foreground">
                    {t('settings.profession_impacts',
                      'This affects your deduction gap analysis and peer benchmarks.')}
                  </span>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* ── Account & Tax tab (all existing content) ──────────────────────── */}
        {activeTab === 'settings' && (<>
        <Card className="shadow-soft border-border/40" data-testid="profile-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="w-5 h-5" />
              {t('profile.title', 'Profile')}
            </CardTitle>
            <CardDescription>{t('profile.personal_info', 'Your personal information')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-center gap-4">
              <UserAvatar user={user} className="w-20 h-20 ring-2 ring-emerald-100" fallbackClassName="text-xl" testId="settings-user-avatar" />
              <div>
                <h3 className="text-lg font-semibold">{user?.name}</h3>
                <p className="text-muted-foreground">{user?.email}</p>
                {user?.auth_provider === 'google' && (
                  <p className="text-xs text-stone-500 mt-1" data-testid="settings-google-photo-note">
                    {user?.picture ? 'Profile photo synced from your Google account' : 'Signed in with Google'}
                  </p>
                )}
              </div>
            </div>

            <Separator />

            <div className="grid gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">{t('common.full_name', 'Full Name')}</Label>
                <Input id="name" value={user?.name || ''} disabled className="bg-muted" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">{t('common.email', 'Email')}</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input id="email" value={user?.email || ''} disabled className="pl-10 bg-muted" />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <CpaContactCard />

        {/* Language & Preferences Card */}
        <Card className="shadow-soft border-border/40" data-testid="preferences-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Globe className="w-5 h-5" />
              {t('settings.language')}
            </CardTitle>
            <CardDescription>{t('settings.language_description', 'Choose your preferred language for the application')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <LanguageSwitcher variant="dropdown" />
          </CardContent>
        </Card>

        {/* Tax Profile Card */}
        <Card className="shadow-soft border-border/40" data-testid="tax-profile-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              {t('profile.tax_profile', 'Tax Profile')}
            </CardTitle>
            <CardDescription>{t('profile.tax_profile_desc', 'Your filing status and spouse information for accurate tax calculations')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-primary" />
              </div>
            ) : (
              <>
                {/* Filing Status */}
                <div className="space-y-2">
                  <Label>{t('profile.filing_status', 'Filing Status')}</Label>
                  <Select value={filingStatus} onValueChange={handleFilingStatusChange}>
                    <SelectTrigger>
                      <SelectValue placeholder={t('profile.select_filing_status', 'Select filing status')} />
                    </SelectTrigger>
                    <SelectContent>
                      {filingStatusOptions.map(option => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    {t('profile.filing_status_desc', 'This affects your tax brackets and standard deduction amount')}
                  </p>
                </div>

                {/* State Selection */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    {t('profile.state_residence', 'State of Residence')}
                  </Label>
                  <Select value={selectedState} onValueChange={handleStateChange}>
                    <SelectTrigger>
                      <SelectValue placeholder={t('profile.select_state', 'Select your state')} />
                    </SelectTrigger>
                    <SelectContent className="max-h-60">
                      {states.map(state => (
                        <SelectItem key={state.code} value={state.code}>
                          {state.name} {state.rate > 0 ? `(${state.rate}%)` : t('profile.no_state_tax', '(No state tax)')}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {stateInfo && (
                    <div className="flex items-center gap-2 text-sm">
                      <Badge variant={stateInfo.rate === 0 ? "secondary" : "outline"}>
                        {stateInfo.rate === 0 ? (
                          <span className="text-emerald-600">No State Income Tax</span>
                        ) : (
                          <span>State Tax Rate: {stateInfo.rate}%</span>
                        )}
                      </Badge>
                      {stateInfo.rate > 0 && stateInfo.brackets && (
                        <span className="text-xs text-muted-foreground">(Progressive brackets)</span>
                      )}
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground">
                    Used to calculate your estimated state tax liability
                  </p>
                </div>

                {/* W-2 Worker Section */}
                <Separator />
                    
                    <div className="space-y-4">
                      <div className="flex items-center space-x-3">
                        <Checkbox id="w2-income" checked={hasW2Income} onCheckedChange={handleW2IncomeToggle} />
                        <Label htmlFor="w2-income" className="flex items-center gap-2 cursor-pointer">
                          <Briefcase className="w-4 h-4 text-blue-500" />
                          <span>I also have W-2 employment income</span>
                        </Label>
                      </div>
                      <p className="text-xs text-muted-foreground ml-7">Check this if you also have W-2 employment — supports multiple employers across multiple years.</p>

                      {/* Existing W-2 entries list */}
                      {hasW2Income && w2Entries.length > 0 && (
                        <div className="space-y-2">
                          {w2Entries.map(entry => (
                            <div key={entry.entry_id} className="bg-blue-50 rounded-lg p-3 border border-blue-200 flex items-start justify-between gap-3" data-testid={`w2-entry-${entry.entry_id}`}>
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2 flex-wrap mb-1">
                                  <p className="font-medium text-blue-900">{entry.employer_name}</p>
                                  <Badge className="bg-blue-100 text-blue-700 text-xs">{entry.tax_year}</Badge>
                                  {(!entry.taxpayer || entry.taxpayer === 'filer')
                                    ? <Badge className="bg-indigo-100 text-indigo-700 text-xs" data-testid={`w2-taxpayer-${entry.entry_id}`}>Filer</Badge>
                                    : <Badge className="bg-amber-100 text-amber-700 text-xs" data-testid={`w2-taxpayer-${entry.entry_id}`}>Spouse</Badge>
                                  }
                                  {entry.employer_state && <Badge variant="outline" className="text-xs border-blue-300 text-blue-600">{entry.employer_state}</Badge>}
                                  {entry.retirement_plan && <Badge className="bg-emerald-100 text-emerald-700 text-xs">Ret. Plan</Badge>}
                                </div>
                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-0.5 text-xs text-blue-700">
                                  <span>Box 1 Wages: <strong>{formatCurrency(entry.w2_wages)}</strong></span>
                                  <span>Fed Withheld: <strong>{formatCurrency(entry.federal_withholding)}</strong></span>
                                  {entry.ss_wages > 0 && <span>SS Wages: <strong>{formatCurrency(entry.ss_wages)}</strong></span>}
                                  {entry.ss_tax_withheld > 0 && <span>SS Tax: <strong>{formatCurrency(entry.ss_tax_withheld)}</strong></span>}
                                  {entry.medicare_wages > 0 && <span>Medicare Wages: <strong>{formatCurrency(entry.medicare_wages)}</strong></span>}
                                  {entry.medicare_tax_withheld > 0 && <span>Medicare Tax: <strong>{formatCurrency(entry.medicare_tax_withheld)}</strong></span>}
                                  {entry.state_withholding > 0 && <span>State Tax: <strong>{formatCurrency(entry.state_withholding)}</strong></span>}
                                  {entry.retirement_401k > 0 && <span>401(k): <strong>{formatCurrency(entry.retirement_401k)}</strong></span>}
                                  {entry.hsa_contributions > 0 && <span>HSA: <strong>{formatCurrency(entry.hsa_contributions)}</strong></span>}
                                </div>
                              </div>
                              <div className="flex gap-1 flex-shrink-0">
                                <Button variant="ghost" size="icon" onClick={() => handleEditW2Entry(entry)} data-testid={`edit-w2-${entry.entry_id}`}
                                  className="text-blue-500 hover:text-blue-700 hover:bg-blue-50">
                                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                                </Button>
                                <Button variant="ghost" size="icon" onClick={() => handleDeleteW2Entry(entry.entry_id)} data-testid={`delete-w2-${entry.entry_id}`}
                                  className={confirmDeleteAction === `w2entry-${entry.entry_id}` ? "bg-red-500 text-white hover:bg-red-600" : "text-red-500 hover:text-red-700 hover:bg-red-50"}>
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Add W-2 button + form */}
                      {hasW2Income && (
                        !showW2Form ? (
                          <Button variant="outline" size="sm" onClick={() => setShowW2Form(true)} data-testid="add-w2-btn">
                            <Plus className="w-4 h-4 mr-2" />Add W-2 Entry
                          </Button>
                        ) : (
                          <div className="space-y-5 p-4 border rounded-lg bg-white max-h-[70vh] overflow-y-auto">
                            <div className="flex items-center justify-between sticky top-0 z-10 bg-white pb-2 border-b">
                              <h4 className="font-medium flex items-center gap-2">
                                <Briefcase className="w-4 h-4" />
                                {editingW2Id ? 'Edit W-2 Entry' : 'W-2 Entry — IRS Standard Fields'}
                              </h4>
                              <Button variant="ghost" size="sm" onClick={() => { setShowW2Form(false); setEditingW2Id(null); }}>Cancel</Button>
                            </div>

                            {/* ── Taxpayer Assignment ─────────────────────── */}
                            <div>
                              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">This W-2 belongs to</p>
                              <div className="flex gap-2">
                                <button
                                  type="button"
                                  data-testid="w2-taxpayer-filer-btn"
                                  onClick={() => setW2Form({...w2Form, taxpayer: 'filer'})}
                                  className={`flex-1 py-2 px-4 rounded-md text-sm font-semibold border-2 transition-all ${
                                    w2Form.taxpayer === 'filer'
                                      ? 'bg-indigo-600 text-white border-indigo-600'
                                      : 'bg-white text-gray-600 border-gray-200 hover:border-indigo-300'
                                  }`}
                                >
                                  Primary Filer
                                </button>
                                <button
                                  type="button"
                                  data-testid="w2-taxpayer-spouse-btn"
                                  onClick={() => setW2Form({...w2Form, taxpayer: 'spouse'})}
                                  className={`flex-1 py-2 px-4 rounded-md text-sm font-semibold border-2 transition-all ${
                                    w2Form.taxpayer === 'spouse'
                                      ? 'bg-amber-500 text-white border-amber-500'
                                      : 'bg-white text-gray-600 border-gray-200 hover:border-amber-300'
                                  }`}
                                >
                                  Spouse
                                </button>
                              </div>
                              {w2Form.taxpayer === 'spouse' && (
                                <p className="text-xs text-amber-600 mt-1.5">
                                  This W-2 will be attributed to the spouse on the joint return.
                                </p>
                              )}
                            </div>

                            {/* Employer / Year / Employee */}
                            <div>
                              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Employer &amp; Employee</p>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div className="sm:col-span-2 space-y-1">
                                  <Label className="text-xs">Employer Name * (Box c)</Label>
                                  <Input value={w2Form.employer_name} onChange={e => setW2Form({...w2Form, employer_name: e.target.value})} placeholder="Company name" />
                                </div>
                                <div className="space-y-1">
                                  <Label className="text-xs">Tax Year *</Label>
                                  <Select value={w2TaxYear.toString()} onValueChange={v => setW2TaxYear(parseInt(v))}>
                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                    <SelectContent>{Array.from({length: 25}, (_, i) => new Date().getFullYear() - 25 + i).map(y => <SelectItem key={y} value={y.toString()}>{y}</SelectItem>)}</SelectContent>
                                  </Select>
                                </div>
                                <div className="space-y-1">
                                  <Label className="text-xs">Employer EIN (Box b)</Label>
                                  <Input value={w2Form.employer_ein} onChange={e => setW2Form({...w2Form, employer_ein: e.target.value})} placeholder="XX-XXXXXXX" />
                                </div>
                                <div className="sm:col-span-2 space-y-1">
                                  <Label className="text-xs">Employer Address (Box c)</Label>
                                  <Input value={w2Form.employer_address} onChange={e => setW2Form({...w2Form, employer_address: e.target.value})} placeholder="Street, City, State, ZIP" />
                                </div>
                                <div className="space-y-1">
                                  <Label className="text-xs">Employee SSN Last 4 (Box e)</Label>
                                  <Input value={w2Form.employee_ssn_last4} onChange={e => setW2Form({...w2Form, employee_ssn_last4: e.target.value.replace(/\D/g,'').slice(0,4)})} placeholder="XXXX" maxLength={4} />
                                </div>
                              </div>
                            </div>

                            {/* Boxes 1-2: Federal Income */}
                            <div>
                              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Federal Income (Boxes 1–2)</p>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                {[{label:'Wages, Tips (Box 1) *',key:'w2_wages'},{label:'Federal Withheld (Box 2)',key:'federal_withholding'}].map(({label,key}) => (
                                  <div key={key} className="space-y-1">
                                    <Label className="text-xs">{label}</Label>
                                    <div className="relative"><DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                                      <Input type="number" className="pl-8 text-sm" value={w2Form[key] || ''} onChange={e => setW2Form({...w2Form, [key]: parseFloat(e.target.value) || 0})} placeholder="0" /></div>
                                  </div>
                                ))}
                              </div>
                            </div>

                            {/* Boxes 3-8: Social Security & Medicare */}
                            <div>
                              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Social Security &amp; Medicare (Boxes 3–8)</p>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                {[
                                  {label:'SS Wages (Box 3)',key:'ss_wages'},{label:'SS Tax Withheld (Box 4)',key:'ss_tax_withheld'},
                                  {label:'Medicare Wages (Box 5)',key:'medicare_wages'},{label:'Medicare Tax Withheld (Box 6)',key:'medicare_tax_withheld'},
                                  {label:'SS Tips (Box 7)',key:'ss_tips'},{label:'Allocated Tips (Box 8)',key:'allocated_tips'},
                                ].map(({label,key}) => (
                                  <div key={key} className="space-y-1">
                                    <Label className="text-xs">{label}</Label>
                                    <div className="relative"><DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                                      <Input type="number" className="pl-8 text-sm" value={w2Form[key] || ''} onChange={e => setW2Form({...w2Form, [key]: parseFloat(e.target.value) || 0})} placeholder="0" /></div>
                                  </div>
                                ))}
                              </div>
                            </div>

                            {/* Boxes 10-11 */}
                            <div>
                              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Other Federal (Boxes 10–11)</p>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                {[{label:'Dependent Care Benefits (Box 10)',key:'dependent_care_benefits'},{label:'Nonqualified Plans (Box 11)',key:'nonqualified_plans'}].map(({label,key}) => (
                                  <div key={key} className="space-y-1">
                                    <Label className="text-xs">{label}</Label>
                                    <div className="relative"><DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                                      <Input type="number" className="pl-8 text-sm" value={w2Form[key] || ''} onChange={e => setW2Form({...w2Form, [key]: parseFloat(e.target.value) || 0})} placeholder="0" /></div>
                                  </div>
                                ))}
                              </div>
                            </div>

                            {/* Box 12 Codes */}
                            <div>
                              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Box 12 — Retirement &amp; Benefits Codes</p>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                {[
                                  {label:'Code D — Traditional 401(k)',key:'retirement_401k'},
                                  {label:'Code AA — Roth 401(k)',key:'roth_401k'},
                                  {label:'Code BB — Roth 403(b)',key:'roth_403b'},
                                  {label:'Code W — HSA (employer + employee)',key:'hsa_contributions'},
                                  {label:'Code DD — Employer health coverage cost',key:'employer_health_coverage'},
                                  {label:'IRA Contributions (Schedule 1)',key:'retirement_ira'},
                                  {label:'FSA Contributions',key:'fsa_contributions'},
                                ].map(({label,key}) => (
                                  <div key={key} className="space-y-1">
                                    <Label className="text-xs">{label}</Label>
                                    <div className="relative"><DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                                      <Input type="number" className="pl-8 text-sm" value={w2Form[key] || ''} onChange={e => setW2Form({...w2Form, [key]: parseFloat(e.target.value) || 0})} placeholder="0" /></div>
                                  </div>
                                ))}
                              </div>
                            </div>

                            {/* Box 13 Checkboxes */}
                            <div>
                              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Box 13 — Checkboxes</p>
                              <div className="flex flex-wrap gap-4">
                                {[{label:'Statutory Employee',key:'statutory_employee'},{label:'Retirement Plan Participant',key:'retirement_plan'},{label:'Third-Party Sick Pay',key:'third_party_sick_pay'}].map(({label,key}) => (
                                  <div key={key} className="flex items-center gap-2">
                                    <Checkbox id={key} checked={w2Form[key]} onCheckedChange={v => setW2Form({...w2Form, [key]: v})} />
                                    <Label htmlFor={key} className="text-xs cursor-pointer">{label}</Label>
                                  </div>
                                ))}
                              </div>
                            </div>

                            {/* Box 14 */}
                            <div>
                              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Box 14 — Other</p>
                              <Input value={w2Form.box14_other} onChange={e => setW2Form({...w2Form, box14_other: e.target.value})} placeholder="e.g., VPDI:125.00, SDI:250.00" className="text-sm" />
                              <p className="text-xs text-muted-foreground mt-1">Comma-separated entries if multiple (e.g., SDI, union dues)</p>
                            </div>

                            {/* Boxes 15-20: State & Local */}
                            <div>
                              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">State &amp; Local (Boxes 15–20)</p>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div className="space-y-1">
                                  <Label className="text-xs">State (Box 15)</Label>
                                  <Input value={w2Form.employer_state} onChange={e => setW2Form({...w2Form, employer_state: e.target.value.toUpperCase().slice(0,2)})} placeholder="CA" maxLength={2} className="text-sm" />
                                </div>
                                <div className="space-y-1">
                                  <Label className="text-xs">Employer State ID (Box 15)</Label>
                                  <Input value={w2Form.employer_state_id} onChange={e => setW2Form({...w2Form, employer_state_id: e.target.value})} placeholder="State employer ID" className="text-sm" />
                                </div>
                                {[
                                  {label:'State Wages (Box 16)',key:'state_wages'},{label:'State Tax Withheld (Box 17)',key:'state_withholding'},
                                  {label:'Local Wages (Box 18)',key:'local_wages'},{label:'Local Tax Withheld (Box 19)',key:'local_tax_withheld'},
                                ].map(({label,key}) => (
                                  <div key={key} className="space-y-1">
                                    <Label className="text-xs">{label}</Label>
                                    <div className="relative"><DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                                      <Input type="number" className="pl-8 text-sm" value={w2Form[key] || ''} onChange={e => setW2Form({...w2Form, [key]: parseFloat(e.target.value) || 0})} placeholder="0" /></div>
                                  </div>
                                ))}
                                <div className="space-y-1">
                                  <Label className="text-xs">Locality Name (Box 20)</Label>
                                  <Input value={w2Form.locality_name} onChange={e => setW2Form({...w2Form, locality_name: e.target.value})} placeholder="e.g., New York City" className="text-sm" />
                                </div>
                              </div>
                            </div>

                            <Input value={w2Form.notes} onChange={e => setW2Form({...w2Form, notes: e.target.value})} placeholder="Notes (optional)" className="text-sm" />
                            <Button onClick={handleSaveW2Info} disabled={saving} data-testid="save-w2-btn" className="w-full sm:w-auto">
                              {saving ? 'Saving...' : editingW2Id ? 'Update W-2 Entry' : t('settings.save_w2', 'Save W-2 Entry')}
                            </Button>
                          </div>
                        )
                      )}
                    </div>

                    {/* W-2 Documents Section */}
                    {hasW2Income && (
                      <>
                        <Separator />
                        
                        <div className="space-y-4">
                          <div className="flex items-center justify-between">
                            <div>
                              <h3 className="font-semibold flex items-center gap-2">
                                <FileText className="w-5 h-5" />
                                W-2 Documents
                              </h3>
                              <p className="text-sm text-muted-foreground">
                                Upload W-2 forms for automatic data extraction
                              </p>
                            </div>
                            <label className="cursor-pointer">
                              <input
                                type="file"
                                accept=".pdf"
                                className="hidden"
                                onChange={handleW2DocumentUpload}
                                disabled={uploading}
                              />
                              <Button variant="outline" size="sm" disabled={uploading} asChild>
                                <span>
                                  {uploading ? (
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                  ) : (
                                    <Upload className="w-4 h-4 mr-2" />
                                  )}
                                  {t('settings.upload_w2', 'Upload W-2')}
                                </span>
                              </Button>
                            </label>
                          </div>

                          {w2Documents.length > 0 ? (
                            <div className="space-y-2">
                              {w2Documents.map((doc) => (
                                <div key={doc.document_id} className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
                                  <div className="flex items-center gap-3">
                                    <FileText className="w-5 h-5 text-blue-600" />
                                    <div>
                                      <p className="font-medium text-sm text-blue-900">{doc.original_filename}</p>
                                      <p className="text-xs text-blue-600">
                                        Uploaded {new Date(doc.uploaded_at).toLocaleDateString()}
                                      </p>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleExtractW2Data(doc.document_id)}
                                      disabled={extracting === doc.document_id}
                                      className="text-blue-600 hover:text-blue-800"
                                    >
                                      {extracting === doc.document_id ? (
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                      ) : (
                                        <Eye className="w-4 h-4" />
                                      )}
                                      <span className="ml-1">Extract</span>
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleDeleteW2Document(doc.document_id)}
                                      className={confirmDeleteAction === `w2doc-${doc.document_id}` ? "bg-red-500 text-white hover:bg-red-600" : "text-red-500 hover:text-red-700"}
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="text-center py-6 bg-muted/30 rounded-lg border border-dashed">
                              <FileText className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                              <p className="text-sm text-muted-foreground">No W-2 documents uploaded yet</p>
                              <p className="text-xs text-muted-foreground">Upload your W-2s to auto-fill the form above</p>
                            </div>
                          )}
                        </div>
                      </>
                    )}

                {/* 1099 Income Section - For freelancers/contractors */}
                <Separator />
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Receipt className="w-5 h-5 text-emerald-500" />
                      <div>
                        <h3 className="font-semibold">1099 Income</h3>
                        <p className="text-xs text-muted-foreground">Upload 1099s you received from clients/platforms</p>
                      </div>
                    </div>
                    {form1099Summary.count > 0 && (
                      <Badge variant="secondary" className="bg-emerald-100 text-emerald-700">
                        {form1099Summary.count} form{form1099Summary.count > 1 ? 's' : ''} • {formatCurrency(form1099Summary.total)}
                      </Badge>
                    )}
                  </div>

                  {/* 1099 Summary Card */}
                  {form1099Summary.count > 0 && (
                    <div className="bg-emerald-50 rounded-lg p-4 border border-emerald-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-emerald-700">Total 1099 Income ({new Date().getFullYear()})</p>
                          <p className="text-2xl font-bold text-emerald-700">{formatCurrency(form1099Summary.total)}</p>
                        </div>
                        <TrendingUp className="w-8 h-8 text-emerald-500" />
                      </div>
                    </div>
                  )}

                  <div className="border-2 border-dashed border-emerald-200 rounded-lg p-6 text-center hover:border-emerald-400 transition-colors">
                    <input type="file" id="form1099-upload" className="hidden" accept=".pdf" onChange={handle1099Upload} disabled={uploading1099} />
                    <label htmlFor="form1099-upload" className="cursor-pointer">
                      {uploading1099 ? <Loader2 className="w-10 h-10 mx-auto text-emerald-500 animate-spin mb-2" /> : <Upload className="w-10 h-10 mx-auto text-emerald-500 mb-2" />}
                      <p className="font-medium text-emerald-700">{uploading1099 ? 'Uploading...' : 'Upload 1099 Document'}</p>
                      <p className="text-xs text-muted-foreground mt-1">PDF files only · 1099-NEC, 1099-K, 1099-MISC</p>
                    </label>
                  </div>

                  {/* Manual 1099 entry */}
                  <div>
                    <Button variant="outline" size="sm" onClick={() => setShowManual1099Form(v => !v)} data-testid="add-manual-1099-btn">
                      <Plus className="w-4 h-4 mr-2" />{showManual1099Form ? 'Cancel' : 'Add 1099 Manually'}
                    </Button>
                    {showManual1099Form && (
                      <div className="mt-3 p-4 border rounded-lg bg-white space-y-3">
                        <h4 className="font-medium text-sm">Manual 1099 Entry</h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div className="space-y-1 sm:col-span-2">
                            <Label className="text-xs">Payer Name *</Label>
                            <Input value={manual1099Form.payer_name} onChange={e => setManual1099Form({...manual1099Form, payer_name: e.target.value})} placeholder="e.g. Upwork, Stripe" />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Tax Year *</Label>
                            <Select value={manual1099Form.tax_year.toString()} onValueChange={v => setManual1099Form({...manual1099Form, tax_year: parseInt(v)})}>
                              <SelectTrigger><SelectValue /></SelectTrigger>
                              <SelectContent>{Array.from({length: 25}, (_, i) => new Date().getFullYear() - 25 + i).map(y => <SelectItem key={y} value={y.toString()}>{y}</SelectItem>)}</SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">1099 Type</Label>
                            <Select value={manual1099Form.box_type} onValueChange={v => setManual1099Form({...manual1099Form, box_type: v})}>
                              <SelectTrigger><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {['NEC','MISC','K','INT','DIV','R','B','W2G'].map(boxT => <SelectItem key={boxT} value={boxT}>1099-{boxT}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Amount *</Label>
                            <div className="relative"><DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                              <Input type="number" className="pl-9" value={manual1099Form.amount || ''} onChange={e => setManual1099Form({...manual1099Form, amount: parseFloat(e.target.value) || 0})} placeholder="0" /></div>
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Federal Withheld</Label>
                            <div className="relative"><DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                              <Input type="number" className="pl-9" value={manual1099Form.federal_withholding || ''} onChange={e => setManual1099Form({...manual1099Form, federal_withholding: parseFloat(e.target.value) || 0})} placeholder="0" /></div>
                          </div>
                          <div className="space-y-1 sm:col-span-2">
                            <Label className="text-xs">Notes</Label>
                            <Input value={manual1099Form.notes} onChange={e => setManual1099Form({...manual1099Form, notes: e.target.value})} placeholder="Optional notes" />
                          </div>
                        </div>
                        <Button size="sm" onClick={handleAddManual1099} data-testid="save-manual-1099-btn">Save 1099 Entry</Button>
                      </div>
                    )}
                  </div>

                  {/* 1099 Documents List */}
                  {form1099Documents.length > 0 ? (
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-muted-foreground">Uploaded 1099s</p>
                      {form1099Documents.map((doc) => (
                        <div key={doc.document_id} className="flex items-center justify-between p-3 bg-white rounded-lg border">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                              <FileText className="w-5 h-5 text-emerald-600" />
                            </div>
                            <div>
                              <p className="font-medium text-sm">{doc.filename}</p>
                              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                <span>{doc.form_type || '1099'}</span>
                                {doc.payer_name && <span>• {doc.payer_name}</span>}
                                {doc.amount && <span className="text-emerald-600 font-medium">• {formatCurrency(doc.amount)}</span>}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {!doc.extracted && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleExtract1099(doc.document_id)}
                                className="text-blue-600 hover:text-blue-800"
                                disabled={extracting1099 === doc.document_id}
                              >
                                {extracting1099 === doc.document_id ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Eye className="w-4 h-4" />
                                )}
                                <span className="ml-1">Extract</span>
                              </Button>
                            )}
                            {doc.extracted && (
                              <Badge variant="secondary" className="bg-emerald-100 text-emerald-700">
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Extracted
                              </Badge>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete1099Document(doc.document_id)}
                              className={confirmDeleteAction === `1099doc-${doc.document_id}` ? "bg-red-500 text-white hover:bg-red-600" : "text-red-500 hover:text-red-700"}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-4 bg-muted/30 rounded-lg border border-dashed">
                      <Receipt className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                      <p className="text-sm text-muted-foreground">No 1099s uploaded yet</p>
                      <p className="text-xs text-muted-foreground">Upload your 1099-NEC, 1099-K, or 1099-MISC forms</p>
                    </div>
                  )}
                </div>

                {/* Spouse Section - Show when filing jointly */}
                {(filingStatus === 'married_filing_jointly' || spouseInfo) && (
                  <>
                    <Separator />
                    
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Heart className="w-5 h-5 text-pink-500" />
                          <h3 className="font-semibold">Spouse Information</h3>
                        </div>
                        {spouseInfo && !showSpouseForm && (
                          <div className="flex gap-2">
                            <Button variant="outline" size="sm" onClick={() => setShowSpouseForm(true)}>
                              Edit
                            </Button>
                            <Button
                              variant={confirmDeleteAction === "spouseinfo" ? "destructive" : "outline"}
                              size="sm"
                              onClick={handleDeleteSpouseInfo}
                              data-testid="delete-spouse-info-btn"
                            >
                              {confirmDeleteAction === "spouseinfo" ? "Confirm Remove?" : <Trash2 className="w-4 h-4" />}
                            </Button>
                          </div>
                        )}
                      </div>

                      {/* Spouse Display Card */}
                      {spouseInfo && !showSpouseForm && (
                        <div className="bg-muted/50 rounded-lg p-4 space-y-3">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium">{spouseInfo.name}</p>
                              {spouseInfo.employer_name && (
                                <p className="text-sm text-muted-foreground">
                                  Employed at {spouseInfo.employer_name}
                                </p>
                              )}
                            </div>
                            <Badge variant="secondary">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Active
                            </Badge>
                          </div>
                          
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 pt-2">
                            <div>
                              <p className="text-xs text-muted-foreground">W-2 Wages</p>
                              <p className="font-semibold text-emerald-600">{formatCurrency(spouseInfo.w2_wages)}</p>
                            </div>
                            <div>
                              <p className="text-xs text-muted-foreground">Federal Withheld</p>
                              <p className="font-semibold">{formatCurrency(spouseInfo.federal_withholding)}</p>
                            </div>
                            <div>
                              <p className="text-xs text-muted-foreground">401(k)</p>
                              <p className="font-semibold">{formatCurrency(spouseInfo.retirement_401k)}</p>
                            </div>
                            <div>
                              <p className="text-xs text-muted-foreground">HSA</p>
                              <p className="font-semibold">{formatCurrency(spouseInfo.hsa_contributions)}</p>
                            </div>
                            <div>
                              <p className="text-xs text-muted-foreground">Self-Employment</p>
                              <p className="font-semibold">{formatCurrency(spouseInfo.self_employment_income)}</p>
                            </div>
                            <div>
                              <p className="text-xs text-muted-foreground">Other Income</p>
                              <p className="font-semibold">{formatCurrency(spouseInfo.other_income)}</p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Spouse Form */}
                      {(showSpouseForm || (!spouseInfo && filingStatus === 'married_filing_jointly')) && (
                        <div className="space-y-4 p-4 border rounded-lg bg-white">
                          <div className="flex items-center gap-3 mb-2">
                            <Label className="text-gray-700">Tax Year:</Label>
                            <Select value={spouseTaxYear.toString()} onValueChange={(v) => setSpouseTaxYear(parseInt(v))}>
                              <SelectTrigger className="w-28">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {Array.from({length: 7}, (_, i) => new Date().getFullYear() - 5 + i).map(y => (
                                  <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label className="text-gray-700">Spouse Name *</Label>
                              <Input
                                value={spouseForm.name}
                                onChange={(e) => setSpouseForm({...spouseForm, name: e.target.value})}
                                placeholder="Full legal name"
                                className="bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label className="text-gray-700">SSN (Last 4 digits)</Label>
                              <Input
                                value={spouseForm.ssn_last4}
                                onChange={(e) => setSpouseForm({...spouseForm, ssn_last4: e.target.value.slice(0, 4)})}
                                placeholder="1234"
                                maxLength={4}
                                className="bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label className="text-gray-700">Employer Name</Label>
                              <Input
                                value={spouseForm.employer_name}
                                onChange={(e) => setSpouseForm({...spouseForm, employer_name: e.target.value})}
                                placeholder="Company name"
                                className="bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label className="text-gray-700">W-2 Wages</Label>
                              <div className="relative">
                                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                                <Input
                                  type="number"
                                  className="pl-9 bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
                                  value={spouseForm.w2_wages || ''}
                                  onChange={(e) => setSpouseForm({...spouseForm, w2_wages: parseFloat(e.target.value) || 0})}
                                  placeholder="0"
                                />
                              </div>
                            </div>
                            <div className="space-y-2">
                              <Label className="text-gray-700">Federal Tax Withheld</Label>
                              <div className="relative">
                                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                                <Input
                                  type="number"
                                  className="pl-9 bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
                                  value={spouseForm.federal_withholding || ''}
                                  onChange={(e) => setSpouseForm({...spouseForm, federal_withholding: parseFloat(e.target.value) || 0})}
                                  placeholder="0"
                                />
                              </div>
                            </div>
                            <div className="space-y-2">
                              <Label className="text-gray-700">State Tax Withheld</Label>
                              <div className="relative">
                                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                                <Input
                                  type="number"
                                  className="pl-9 bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
                                  value={spouseForm.state_withholding || ''}
                                  onChange={(e) => setSpouseForm({...spouseForm, state_withholding: parseFloat(e.target.value) || 0})}
                                  placeholder="0"
                                />
                              </div>
                            </div>
                          </div>

                          <Separator />
                          
                          <div>
                            <h4 className="font-medium mb-3 flex items-center gap-2 text-gray-800">
                              <PiggyBank className="w-4 h-4" />
                              Retirement & Savings
                            </h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div className="space-y-2">
                                <Label className="text-gray-700">401(k) Contributions</Label>
                                <div className="relative">
                                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                                  <Input
                                    type="number"
                                    className="pl-9 bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
                                    value={spouseForm.retirement_401k || ''}
                                    onChange={(e) => setSpouseForm({...spouseForm, retirement_401k: parseFloat(e.target.value) || 0})}
                                    placeholder="0"
                                  />
                                </div>
                              </div>
                              <div className="space-y-2">
                                <Label className="text-gray-700">IRA Contributions</Label>
                                <div className="relative">
                                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                                  <Input
                                    type="number"
                                    className="pl-9 bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
                                    value={spouseForm.retirement_ira || ''}
                                    onChange={(e) => setSpouseForm({...spouseForm, retirement_ira: parseFloat(e.target.value) || 0})}
                                    placeholder="0"
                                  />
                                </div>
                              </div>
                              <div className="space-y-2">
                                <Label className="text-gray-700">HSA Contributions</Label>
                                <div className="relative">
                                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                                  <Input
                                    type="number"
                                    className="pl-9 bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
                                    value={spouseForm.hsa_contributions || ''}
                                    onChange={(e) => setSpouseForm({...spouseForm, hsa_contributions: parseFloat(e.target.value) || 0})}
                                    placeholder="0"
                                  />
                                </div>
                              </div>
                              <div className="space-y-2">
                                <Label className="text-gray-700">FSA Contributions</Label>
                                <div className="relative">
                                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                                  <Input
                                    type="number"
                                    className="pl-9 bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
                                    value={spouseForm.fsa_contributions || ''}
                                    onChange={(e) => setSpouseForm({...spouseForm, fsa_contributions: parseFloat(e.target.value) || 0})}
                                    placeholder="0"
                                  />
                                </div>
                              </div>
                            </div>
                          </div>

                          <Separator />
                          
                          <div>
                            <h4 className="font-medium mb-3 text-gray-800">Other Income</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div className="space-y-2">
                                <Label className="text-gray-700">Self-Employment Income</Label>
                                <div className="relative">
                                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                                  <Input
                                    type="number"
                                    className="pl-9 bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
                                    value={spouseForm.self_employment_income || ''}
                                    onChange={(e) => setSpouseForm({...spouseForm, self_employment_income: parseFloat(e.target.value) || 0})}
                                    placeholder="0"
                                  />
                                </div>
                              </div>
                              <div className="space-y-2">
                                <Label className="text-gray-700">Other Income (1099s, etc.)</Label>
                                <div className="relative">
                                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                                  <Input
                                    type="number"
                                    className="pl-9 bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
                                    value={spouseForm.other_income || ''}
                                    onChange={(e) => setSpouseForm({...spouseForm, other_income: parseFloat(e.target.value) || 0})}
                                    placeholder="0"
                                  />
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="space-y-2">
                            <Label className="text-gray-700">Notes</Label>
                            <Input
                              value={spouseForm.notes}
                              onChange={(e) => setSpouseForm({...spouseForm, notes: e.target.value})}
                              placeholder="Any additional notes..."
                              className="bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
                            />
                          </div>

                          <div className="flex gap-2 pt-2">
                            <Button onClick={handleSaveSpouseInfo} disabled={saving}>
                              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                              Save Spouse Information
                            </Button>
                            {spouseInfo && (
                              <Button variant="outline" onClick={() => setShowSpouseForm(false)}>
                                Cancel
                              </Button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Spouse Documents Section */}
                    <Separator />
                    
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="font-semibold flex items-center gap-2">
                            <FileText className="w-5 h-5" />
                            Spouse Tax Documents
                          </h3>
                          <p className="text-sm text-muted-foreground">
                            Upload W-2s and other tax forms · supports multiple years
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Select value={spouseDocYear.toString()} onValueChange={v => setSpouseDocYear(parseInt(v))}>
                            <SelectTrigger className="w-28 h-8 text-xs"><SelectValue /></SelectTrigger>
                            <SelectContent>{Array.from({length: 25}, (_, i) => new Date().getFullYear() - 25 + i).map(y => <SelectItem key={y} value={y.toString()}>{y}</SelectItem>)}</SelectContent>
                          </Select>
                          <label className="cursor-pointer">
                            <input type="file" accept=".pdf" className="hidden" onChange={handleSpouseDocumentUpload} disabled={uploading} />
                            <Button variant="outline" size="sm" disabled={uploading} asChild>
                              <span>
                                {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                                {t('settings.upload_w2', 'Upload W-2')}
                              </span>
                            </Button>
                          </label>
                        </div>
                      </div>

                      {spouseDocuments.length > 0 ? (
                        <div className="space-y-2">
                          {spouseDocuments.map(doc => (
                            <div key={doc.document_id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                              <div className="flex items-center gap-3">
                                <FileText className="w-8 h-8 text-muted-foreground" />
                                <div>
                                  <p className="font-medium">{doc.filename}</p>
                                  <p className="text-xs text-muted-foreground">
                                    {doc.document_type.toUpperCase()} • Tax Year {doc.tax_year}
                                    {doc.extracted_data && (
                                      <Badge variant="secondary" className="ml-2">
                                        <CheckCircle className="w-3 h-3 mr-1" />
                                        Data Extracted
                                      </Badge>
                                    )}
                                  </p>
                                </div>
                              </div>
                              <div className="flex gap-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => window.open(`${API}/spouse/documents/${doc.document_id}/file`, '_blank')}
                                >
                                  <Eye className="w-4 h-4" />
                                </Button>
                                {!doc.extracted_data && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleExtractData(doc.document_id)}
                                    disabled={extracting === doc.document_id}
                                  >
                                    {extracting === doc.document_id ? (
                                      <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                      'Extract Data'
                                    )}
                                  </Button>
                                )}
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeleteDocument(doc.document_id)}
                                  className={confirmDeleteAction === `spousedoc-${doc.document_id}` ? "bg-red-500 text-white hover:bg-red-600" : ""}
                                >
                                  <Trash2 className="w-4 h-4 text-destructive" />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-6 text-muted-foreground border border-dashed rounded-lg">
                          <Upload className="w-8 h-8 mx-auto mb-2 opacity-50" />
                          <p className="text-sm">No spouse documents uploaded yet</p>
                          <p className="text-xs">Upload W-2s to auto-populate spouse income data</p>
                        </div>
                      )}
                    </div>

                    {/* ── Spouse 1099 Section ── */}
                    <Separator />
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="font-semibold flex items-center gap-2">
                            <Receipt className="w-5 h-5 text-amber-500" />
                            Spouse 1099 Income
                          </h3>
                          <p className="text-sm text-muted-foreground">
                            1099-NEC, 1099-K, 1099-MISC and other 1099 forms for your spouse
                          </p>
                        </div>
                        <Button variant="outline" size="sm" onClick={() => setShowSpouseManual1099Form(v => !v)} data-testid="add-spouse-1099-btn">
                          <Plus className="w-4 h-4 mr-2" />{showSpouseManual1099Form ? 'Cancel' : 'Add 1099'}
                        </Button>
                      </div>

                      {/* Manual entry form */}
                      {showSpouseManual1099Form && (
                        <div className="p-4 border rounded-lg bg-white space-y-3">
                          <h4 className="font-medium text-sm">Add Spouse 1099 Entry</h4>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div className="space-y-1 sm:col-span-2">
                              <Label className="text-xs">Payer Name *</Label>
                              <Input value={spouseManual1099Form.payer_name} onChange={e => setSpouseManual1099Form({...spouseManual1099Form, payer_name: e.target.value})} placeholder="e.g. Uber, DoorDash, client name" />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs">Tax Year *</Label>
                              <Select value={spouseManual1099Form.tax_year.toString()} onValueChange={v => setSpouseManual1099Form({...spouseManual1099Form, tax_year: parseInt(v)})}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>{Array.from({length: 25}, (_, i) => new Date().getFullYear() - 25 + i).map(y => <SelectItem key={y} value={y.toString()}>{y}</SelectItem>)}</SelectContent>
                              </Select>
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs">1099 Type</Label>
                              <Select value={spouseManual1099Form.box_type} onValueChange={v => setSpouseManual1099Form({...spouseManual1099Form, box_type: v})}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>{['NEC','MISC','K','INT','DIV','R','B','W2G'].map(t => <SelectItem key={t} value={t}>1099-{t}</SelectItem>)}</SelectContent>
                              </Select>
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs">Amount *</Label>
                              <div className="relative"><DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                <Input type="number" className="pl-9" value={spouseManual1099Form.amount || ''} onChange={e => setSpouseManual1099Form({...spouseManual1099Form, amount: parseFloat(e.target.value) || 0})} placeholder="0" /></div>
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs">Federal Withheld</Label>
                              <div className="relative"><DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                <Input type="number" className="pl-9" value={spouseManual1099Form.federal_withholding || ''} onChange={e => setSpouseManual1099Form({...spouseManual1099Form, federal_withholding: parseFloat(e.target.value) || 0})} placeholder="0" /></div>
                            </div>
                            <div className="space-y-1 sm:col-span-2">
                              <Label className="text-xs">Notes</Label>
                              <Input value={spouseManual1099Form.notes} onChange={e => setSpouseManual1099Form({...spouseManual1099Form, notes: e.target.value})} placeholder="Optional notes" />
                            </div>
                          </div>
                          <Button size="sm" onClick={handleAddSpouseManual1099} data-testid="save-spouse-1099-btn">Save Entry</Button>
                        </div>
                      )}

                      {/* Spouse 1099 entries list */}
                      {spouse1099s.length > 0 ? (
                        <div className="space-y-2" data-testid="spouse-1099-list">
                          {spouse1099s.map(entry => (
                            <div key={entry.document_id} className="flex items-center justify-between p-3 bg-amber-50 border border-amber-100 rounded-lg" data-testid={`spouse-1099-${entry.document_id}`}>
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <p className="font-medium text-sm text-amber-900">{entry.payer_name}</p>
                                  <Badge className="text-xs bg-amber-100 text-amber-700">1099-{entry.box_type}</Badge>
                                  <Badge variant="outline" className="text-xs">{entry.tax_year}</Badge>
                                  {entry.source === 'manual' && <Badge variant="secondary" className="text-xs">Manual</Badge>}
                                </div>
                                <p className="text-xs text-amber-700 mt-0.5">
                                  Amount: ${(entry.amount || 0).toLocaleString()}
                                  {entry.federal_withholding > 0 && ` · Fed withheld: $${entry.federal_withholding.toLocaleString()}`}
                                </p>
                              </div>
                              <Button variant="ghost" size="icon" onClick={() => handleDeleteSpouse1099(entry.document_id)} data-testid={`delete-spouse-1099-${entry.document_id}`}
                                className={confirmDeleteAction === `spouse1099-${entry.document_id}` ? "bg-red-500 text-white hover:bg-red-600 flex-shrink-0" : "text-red-400 hover:text-red-600 hover:bg-red-50 flex-shrink-0"}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          ))}
                          <p className="text-xs text-muted-foreground text-right">
                            {/* Commandment II: total_income from backend DTO */}
                            Total 1099 income: ${spouse1099TotalIncome.toLocaleString()}
                          </p>
                        </div>
                      ) : (
                        <div className="text-center py-5 text-muted-foreground border border-dashed rounded-lg">
                          <Receipt className="w-7 h-7 mx-auto mb-2 opacity-40" />
                          <p className="text-sm">No spouse 1099 entries yet</p>
                          <p className="text-xs">Add 1099-NEC, 1099-K, or other 1099 income for your spouse</p>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Business Card */}
        <Card className="shadow-soft border-border/40" data-testid="business-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building className="w-5 h-5" />
              Business Information
            </CardTitle>
            <CardDescription>Your business details for tax purposes</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="businessType">{t('businesses.business_type', 'Business Type')}</Label>
              <Select value={businessType} onValueChange={setBusinessType}>
                <SelectTrigger data-testid="business-type-select">
                  <SelectValue placeholder={t('profile.select_business_type', 'Select your business type')} />
                </SelectTrigger>
                <SelectContent>
                  {businessTypes.map(type => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {t('profile.business_type_desc', 'This helps provide more relevant tax advice')}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Preferences Card */}
        <Card className="shadow-soft border-border/40" data-testid="preferences-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Globe className="w-5 h-5" />
              {t('settings.preferences', 'Preferences')}
            </CardTitle>
            <CardDescription>{t('settings.customize_experience', 'Customize your experience')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">{t('settings.module_tips', 'Module Tips')}</p>
                <p className="text-sm text-muted-foreground">{t('settings.module_tips_desc', 'Show helpful tips and guidance throughout the app')}</p>
              </div>
              <Button 
                variant="outline" 
                onClick={async () => {
                  try {
                    await axios.post(`${API}/user/module-tips/enable-all`, {}, {
                      headers: getAuthHeaders(),
                      withCredentials: true
                    });
                    toast.success(t('settings.tips_enabled', 'Module tips have been re-enabled'));
                  } catch (error) {
                    toast.error(t('common.error', 'Failed to re-enable tips'));
                  }
                }}
                data-testid="enable-tips-btn"
              >
                {t('settings.reenable_tips', 'Re-enable Tips')}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Danger Zone */}
        <Card className="shadow-soft border-destructive/30" data-testid="danger-card">
          <CardHeader>
            <CardTitle className="text-destructive">Danger Zone</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Sign Out</p>
                <p className="text-sm text-muted-foreground">Sign out of your account</p>
              </div>
              <Button 
                variant="destructive" 
                onClick={handleLogout}
                data-testid="logout-settings-btn"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Sign Out
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Info Card */}
        <Card className="shadow-soft border-border/40 bg-muted/30" data-testid="info-card">
          <CardContent className="p-6">
            <p className="text-sm text-muted-foreground text-center">
              SolelyTax is for educational purposes only. Always consult with a licensed CPA 
              for professional tax advice specific to your situation.
            </p>
          </CardContent>
        </Card>

        {/* End of Account & Tax tab */}
        </>)}

      </div>
    </DashboardLayout>
  );
};

export default Settings;
